import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Music, Loader2, CheckCircle, AlertCircle, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYMENT_POLL_INTERVAL_MS = 2000;
const PAYMENT_POLL_MAX_RETRIES = 10;
const SONG_POLL_INTERVAL_MS = 5000;
const SONG_POLL_TIMEOUT_MS = 300000;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");

  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [songStatus, setSongStatus] = useState("idle");
  const [error, setError] = useState(null);
  const pollCount = useRef(0);
  const hasTriggeredGeneration = useRef(false);

  // Poll payment status
  useEffect(() => {
    if (!sessionId) { setError("No session found"); return; }

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          return true;
        }
        if (data.status === "expired") {
          setPaymentStatus("expired");
          return true;
        }
        pollCount.current++;
        if (pollCount.current >= PAYMENT_POLL_MAX_RETRIES) {
          setPaymentStatus("timeout");
          return true;
        }
        return false;
      } catch (err) {
        console.error("Payment status poll failed:", err);
        pollCount.current++;
        if (pollCount.current >= PAYMENT_POLL_MAX_RETRIES) {
          setPaymentStatus("error");
          return true;
        }
        return false;
      }
    };

    const interval = setInterval(async () => {
      const done = await poll();
      if (done) clearInterval(interval);
    }, PAYMENT_POLL_INTERVAL_MS);

    poll(); // immediate first check
    return () => clearInterval(interval);
  }, [sessionId]);

  // Trigger song generation after payment confirmed
  useEffect(() => {
    if (paymentStatus === "paid" && orderId && !hasTriggeredGeneration.current) {
      hasTriggeredGeneration.current = true;
      setSongStatus("generating");
      axios.post(`${API}/orders/${orderId}/generate`)
        .then(() => {
          setSongStatus("done");
          const checkSong = setInterval(async () => {
            try {
              const { data: order } = await axios.get(`${API}/orders/${orderId}`);
              if (order.song_url) {
                clearInterval(checkSong);
                setSongStatus("ready");
              } else if (order.status === "generation_failed") {
                clearInterval(checkSong);
                setSongStatus("error");
              }
            } catch (err) {
              console.error("Song status poll failed:", err);
            }
          }, SONG_POLL_INTERVAL_MS);
          setTimeout(() => clearInterval(checkSong), SONG_POLL_TIMEOUT_MS);
        })
        .catch((err) => {
          console.error("Song generation trigger failed:", err);
          setSongStatus("error");
        });
    }
  }, [paymentStatus, orderId]);

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      {/* Header */}
      <div className="bg-[#F5F5F5] border-b border-[#E0E0E0]">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center gap-2">
          <Music className="h-5 w-5 text-[#7D1B1B]" strokeWidth={1.5} />
          <span className="font-display text-lg font-bold text-[#2C2C2C]">StorytoSong</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center">
          {paymentStatus === "checking" && (
            <div data-testid="payment-checking" className="space-y-6 animate-fade-in">
              <Loader2 className="h-16 w-16 text-[#7D1B1B] animate-spin mx-auto" />
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C]">Confirming Your Payment...</h1>
              <p className="text-[#5A5A5A]">Please wait while we verify your payment.</p>
            </div>
          )}

          {paymentStatus === "paid" && (
            <div data-testid="payment-success" className="space-y-6 animate-fade-in">
              <div className="w-20 h-20 rounded-full bg-[#E8EFED] flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-[#4F8A6F]" />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C]">Payment Successful!</h1>
              <p className="text-[#5A5A5A]">Your custom song is being created right now.</p>

              {songStatus === "generating" && (
                <div data-testid="song-generating" className="bg-white rounded-2xl p-6 border border-[#E0E0E0] shadow-sm space-y-4 mt-6">
                  <div className="flex items-center justify-center gap-3">
                    <Sparkles className="h-5 w-5 text-[#7D1B1B] animate-pulse" />
                    <span className="font-bold text-[#2C2C2C]">Writing your lyrics...</span>
                  </div>
                  <div className="w-full bg-[#2A2A2A] rounded-full h-2">
                    <div className="bg-[#7D1B1B] h-2 rounded-full animate-pulse" style={{ width: "60%" }} />
                  </div>
                  <p className="text-sm text-[#5A5A5A]">Our songwriter is crafting original lyrics from your story. This takes about a minute.</p>
                </div>
              )}

              {songStatus === "done" && (
                <div data-testid="song-producing" className="bg-white rounded-2xl p-6 border border-[#E0E0E0] shadow-sm space-y-4 mt-6">
                  <div className="flex items-center justify-center gap-3">
                    <Music className="h-5 w-5 text-[#7D1B1B] animate-pulse" />
                    <span className="font-bold text-[#2C2C2C]">Producing your song...</span>
                  </div>
                  <div className="w-full bg-[#2A2A2A] rounded-full h-2">
                    <div className="bg-[#7D1B1B] h-2 rounded-full animate-pulse" style={{ width: "85%" }} />
                  </div>
                  <p className="text-sm text-[#5A5A5A]">Lyrics are ready! Now our AI is bringing them to life with studio-quality vocals and production. This usually takes 1-3 minutes.</p>
                </div>
              )}

              {songStatus === "ready" && (
                <div data-testid="song-ready" className="space-y-4 mt-6">
                  <div className="bg-[#E8EFED] rounded-2xl p-6 border border-[#8DA399]/30">
                    <CheckCircle className="h-8 w-8 text-[#4F8A6F] mx-auto mb-3" />
                    <p className="font-bold text-[#2C2C2C]">Your song is ready!</p>
                  </div>
                  <button data-testid="listen-now-btn" onClick={() => navigate(`/listen/${orderId}`)}
                    className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold text-lg shadow-lg transition-all transform hover:-translate-y-1 w-full">
                    Listen to Your Song
                  </button>
                </div>
              )}

              {songStatus === "error" && (
                <div data-testid="song-error" className="bg-red-50 rounded-2xl p-6 border border-red-200 mt-6">
                  <AlertCircle className="h-8 w-8 text-[#D64545] mx-auto mb-3" />
                  <p className="font-bold text-[#2C2C2C]">There was an issue generating your song.</p>
                  <p className="text-sm text-[#5A5A5A] mt-2">Don&apos;t worry — our team has been notified and will get it sorted. Check your email for updates.</p>
                </div>
              )}
            </div>
          )}

          {(paymentStatus === "expired" || paymentStatus === "timeout" || paymentStatus === "error") && (
            <div data-testid="payment-failed" className="space-y-6 animate-fade-in">
              <AlertCircle className="h-16 w-16 text-[#D64545] mx-auto" />
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C]">
                {paymentStatus === "expired" ? "Session Expired" : "Payment Issue"}
              </h1>
              <p className="text-[#5A5A5A]">
                {paymentStatus === "expired"
                  ? "Your payment session has expired. Please try again."
                  : "We couldn't confirm your payment. Please try again or contact support."}
              </p>
              <button data-testid="try-again-btn" onClick={() => navigate("/quiz")}
                className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold transition-all">
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
